import { maybe, rd, RemoteData } from '@passionware/monads';
import { Capability } from '__types__';
import { zip } from 'lodash';
import {
  Camera,
  Clapperboard,
  FileText,
  GalleryHorizontal,
  Home,
  List,
  PlusIcon
} from 'lucide-react';
import React from 'react';
import { matchPath, useLocation, useRouteMatch } from 'react-router-dom';
import type { EnterpriseTeam } from 'v5/api/enterprise/enterprise.api';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { appRouting } from 'v5/core/routing/appRouting';
import { dashboardPath } from 'v5/core/routing/dashboardRouting';
import { EnterpriseNewTeamMenuItem } from 'v5/features/core/enterprise/EnterpriseNewTeamMenuItem';
import { EnterpriseSidebarEmptyState } from 'v5/features/core/enterprise/EnterpriseSidebarEmptyState';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { MenuCollapsible, MenuItem } from 'v5/patterns/sidebar/Menu';
import { SidebarGroup } from 'v5/patterns/sidebar/Sidebar';
import { cn } from 'v5/platform/dom/cn';
import { ifCan } from 'v5/platform/react/ifCan';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import {
  AccessQuery,
  WithAccessService
} from 'v5/services/front/AccessService/AccessService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';

export type EnterpriseTeamsNavSectionProps = WithServices<
  [WithAccessService, WithProductionService]
> & {
  teamsRd: RemoteData<EnterpriseTeam[]>;
  organizationId: string;
  workspaceSlug: string;
  productionTypes: Array<{ id: number; name: string }> | undefined;
  onCreateProduction: (
    productionTypeId?: number,
    scopeOverride?: {
      enterpriseWorkspaceId?: number;
      enterpriseTeamId?: number;
    }
  ) => void;
  canManage?: boolean;
  onCreateTeam?: () => void;
};

export function EnterpriseTeamsNavSection({
  services,
  teamsRd,
  organizationId,
  workspaceSlug,
  productionTypes,
  onCreateProduction,
  canManage = false,
  onCreateTeam
}: EnterpriseTeamsNavSectionProps) {
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const { pathname } = useLocation();
  const loadedTeams = rd.tryGet(teamsRd) ?? [];
  const teamCans = services.accessService.useCanQueries(
    loadedTeams.map(team =>
      appScopeUtils.identityFromTeam(organizationId, team)
    )
  );
  const newTeamItem =
    canManage && onCreateTeam ? (
      <EnterpriseNewTeamMenuItem onClick={onCreateTeam} />
    ) : null;

  return rd
    .journey(teamsRd)
    .wait(null)
    .catch(() => (
      <SidebarGroup label={t('teamsSectionLabel')}>
        <div className="px-3 py-2 text-[13px] text-fg-muted">
          {t('teamsUnavailable')}
        </div>
      </SidebarGroup>
    ))
    .map(teams => {
      if (teams.length === 0) {
        return newTeamItem ? (
          <SidebarGroup label={t('teamsSectionLabel')}>
            <EnterpriseSidebarEmptyState messageKey="emptyTeams" />
            {newTeamItem}
          </SidebarGroup>
        ) : null;
      }

      return ifCan
        .union()
        .render(parentCan => (
          <SidebarGroup label={t('teamsSectionLabel')}>
            {zip(teams, teamCans).map(([team, teamCan]) => {
              if (maybe.isAbsent(team) || maybe.isAbsent(teamCan)) {
                return null;
              }
              return (
                <React.Fragment key={team.id}>
                  {renderTeamNavCollapsible(parentCan, teamCan, {
                    dashboardLabel: t('teamDashboard'),
                    services,
                    team,
                    organizationId,
                    workspaceSlug,
                    productionTypes,
                    onCreateProduction,
                    pathname
                  })}
                </React.Fragment>
              );
            })}
            {newTeamItem}
          </SidebarGroup>
        ))
        .otherwise(null);
    });
}

interface TeamMenuItemWithCreateButtonProps {
  to: string;
  icon?: React.ReactNode;
  level?: number;
  onCreateClick?: (e: React.MouseEvent) => void;
  createTooltip: string;
  children: React.ReactNode;
  isActiveOverride?: boolean;
  autoSelect?: boolean;
}

function TeamMenuItemWithCreateButton({
  to,
  icon,
  level = 0,
  onCreateClick,
  createTooltip,
  children,
  isActiveOverride,
  autoSelect
}: TeamMenuItemWithCreateButtonProps) {
  return (
    <NavLink to={to} isActiveOverride={isActiveOverride}>
      <MenuItem
        className="group/menu-item relative"
        level={level}
        icon={icon}
        autoSelect={autoSelect}
      >
        {onCreateClick ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="">
                <div
                  role="button"
                  className={cn(
                    'absolute right-1.5 top-1.5 size-7 flex items-center justify-center group-hover/menu-item:opacity-100 opacity-0 transition-all',
                    'rounded bg-transparent! backdrop-blur-3xl hover:bg-bg-emphasis/5!'
                  )}
                  onClick={onCreateClick}
                >
                  <PlusIcon className="size-4 shrink-0" />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">{createTooltip}</TooltipContent>
          </Tooltip>
        ) : null}
        {children}
      </MenuItem>
    </NavLink>
  );
}

type TeamProductionTypeMenuItemProps = WithServices<[WithProductionService]> & {
  productionType: { id: number; name: string };
  href: string;
  typeMatchPath: string;
  detailMatchPath: string;
  organizationId: string;
  onCreateClick?: (productionTypeId: number) => void;
};

function TeamProductionTypeMenuItem({
  services,
  productionType,
  href,
  typeMatchPath,
  detailMatchPath,
  organizationId,
  onCreateClick
}: TeamProductionTypeMenuItemProps) {
  const productionTypeName = productionType.name || 'Production';
  const createTooltip = `Create new ${productionTypeName.toLowerCase()}`;

  const isOnProductionTypePage = useRouteMatch({
    path: typeMatchPath,
    exact: true
  });

  const currentDetailRoute = useRouteMatch<{ id: string }>({
    path: detailMatchPath
  });

  const currentProduction = services.productionService.useProduction(
    maybe.map(currentDetailRoute?.params?.id, Number),
    organizationId
  );
  const isActiveFromProduction = rd.mapOrElse(
    currentProduction,
    production => production.production_type_id === productionType.id,
    false
  );

  const handleCreateClick = React.useCallback(() => {
    onCreateClick?.(productionType.id);
  }, [onCreateClick, productionType.id]);

  return (
    <TeamMenuItemWithCreateButton
      level={2}
      to={href}
      icon={<Camera className="size-4" />}
      onCreateClick={onCreateClick ? handleCreateClick : undefined}
      createTooltip={createTooltip}
      isActiveOverride={!!isOnProductionTypePage || isActiveFromProduction}
    >
      {productionTypeName}
    </TeamMenuItemWithCreateButton>
  );
}

function renderTeamNavCollapsible(
  parentCan: () => boolean,
  teamCan: (query: AccessQuery) => RemoteData<boolean>,
  {
    services,
    team,
    organizationId,
    workspaceSlug,
    productionTypes,
    onCreateProduction,
    pathname,
    dashboardLabel
  }: WithServices<[WithProductionService]> & {
    team: EnterpriseTeam;
    organizationId: string;
    workspaceSlug: string;
    productionTypes: Array<{ id: number; name: string }> | undefined;
    onCreateProduction: (
      productionTypeId?: number,
      scopeOverride?: {
        enterpriseWorkspaceId?: number;
        enterpriseTeamId?: number;
      }
    ) => void;
    pathname: string;
    /** Passed in: this is a plain render helper, with no hook scope of its own. */
    dashboardLabel: string;
  }
) {
  const teamRoutes = appRouting
    .org(organizationId)
    .workspace(workspaceSlug)
    .team(team.slug);
  const isOnTeamRoute = !!matchPath(pathname, {
    path: teamRoutes.pathPrefix(),
    exact: false
  });
  const isOnProductionsRoot = !!matchPath(pathname, {
    path: teamRoutes.productions.root(),
    exact: true
  });
  const isOnTeamProductionRoute = !!matchPath(pathname, {
    path: teamRoutes.productions.root(),
    exact: false
  });

  return ifCan
    .childOf(parentCan)
    .ready(teamCan)
    .render(can => (
      <MenuCollapsible
        icon={
          <EnterpriseTeamIcon
            name={team.name}
            iconUrl={team.iconUrl}
            iconColor={team.iconColor}
          />
        }
        label={team.name}
        defaultOpen={isOnTeamRoute}
        navigateOnExpand={false}
      >
        {can({ action: Capability.PRODUCTION_READ }) && (
          <NavLink
            to={dashboardPath(organizationId, workspaceSlug, {
              teamSlug: team.slug
            })}
          >
            <MenuItem level={1} icon={<Home className="size-4" />}>
              {dashboardLabel}
            </MenuItem>
          </NavLink>
        )}
        {can({ action: Capability.PRODUCTION_READ }) && (
          <MenuCollapsible
            icon={<Camera className="size-4" />}
            label="Productions"
            level={1}
            expandOnChildSelect
            defaultOpen={isOnTeamProductionRoute}
          >
            <TeamMenuItemWithCreateButton
              level={2}
              to={teamRoutes.productions.root()}
              icon={<List className="size-4" />}
              onCreateClick={
                can({ action: Capability.PRODUCTION_CREATE })
                  ? () =>
                      onCreateProduction(undefined, {
                        enterpriseWorkspaceId: team.workspaceId,
                        enterpriseTeamId: team.id
                      })
                  : undefined
              }
              createTooltip="Create new production"
              isActiveOverride={isOnProductionsRoot}
              autoSelect
            >
              All
            </TeamMenuItemWithCreateButton>
            {productionTypes?.map(productionType => (
              <TeamProductionTypeMenuItem
                key={productionType.id}
                services={services}
                productionType={productionType}
                href={teamRoutes.productions.type(productionType.id)}
                typeMatchPath={teamRoutes.productions.type(productionType.id)}
                detailMatchPath={teamRoutes.productions.detail()}
                organizationId={organizationId}
                onCreateClick={
                  can({ action: Capability.PRODUCTION_CREATE })
                    ? typeId =>
                        onCreateProduction(typeId, {
                          enterpriseWorkspaceId: team.workspaceId,
                          enterpriseTeamId: team.id
                        })
                    : undefined
                }
              />
            ))}
          </MenuCollapsible>
        )}
        {can({ action: Capability.SHORTLIST_READ }) && (
          <NavLink to={teamRoutes.shortlists.root()}>
            <MenuItem level={1} icon={<GalleryHorizontal className="size-4" />}>
              Shortlists
            </MenuItem>
          </NavLink>
        )}
        {can({ action: Capability.CALLSHEET_READ }) && (
          <NavLink to={teamRoutes.callsheets.root()}>
            <MenuItem level={1} icon={<FileText className="size-4" />}>
              Callsheets
            </MenuItem>
          </NavLink>
        )}
        {can({
          action: Capability.CONFIGURE_CUSTOM_NIKE_FRONT_DESK
        }) && (
          <NavLink to={teamRoutes.frontdesk.root()}>
            <MenuItem level={1} icon={<Clapperboard className="size-4" />}>
              Front desk
            </MenuItem>
          </NavLink>
        )}
      </MenuCollapsible>
    ))
    .otherwise(null);
}
